// ============================================================
// Motor de cálculo de riesgo ergonómico — Ecuación de NIOSH
// (Manejo Manual de Cargas)
//
// Fuente: NIOSH [1994]. Applications Manual for the Revised NIOSH
// Lifting Equation. DHHS (NIOSH) Publication No. 94-110. Cincinnati, OH:
// U.S. Department of Health and Human Services, Public Health Service,
// Centers for Disease Control and Prevention, National Institute for
// Occupational Safety and Health.
//
// Motor de cálculo puro, sin UI:
//   - Factores multiplicadores individuales (HM, VM, DM, AM, FM, CM)
//   - Ecuación del Límite de Peso Recomendado (RWL)
//   - Evaluación de una tarea de levantamiento (con o sin control
//     significativo en destino) e Índice de Levantamiento (LI)
//
// Las Tablas 1 (Factor de Frecuencia) y 3 (Factor de Agarre) fueron
// extraídas de la fuente y confirmadas celda por celda contra la
// fuente original por el usuario (2026) antes de implementarlas — no
// modificar esas tablas sin repetir esa verificación manual.
// ============================================================

function clamp(valor: number, min: number, max: number): number {
  return Math.min(Math.max(valor, min), max);
}

// ===== Constante de carga =====

// LC: constante de carga (kg). El formulario permite override manual
// con advertencia si el usuario introduce un valor >23.
export const LC_DEFAULT = 23;

// ===== 1. Factor de distancia horizontal (HM) =====

export function calcularHM(H: number): number {
  if (H < 25) return 1;
  if (H > 63) return 0;
  return clamp(25 / H, 0, 1);
}

// ===== 2. Factor de distancia vertical (VM) =====

export function calcularVM(V: number): number {
  if (V > 175) return 0;
  return clamp(1 - 0.003 * Math.abs(V - 75), 0, 1);
}

// ===== 3. Desplazamiento vertical (D) =====

export function calcularD(Vo: number, Vd: number): number {
  const D = Math.abs(Vo - Vd);
  return D > 175 ? 175 : D;
}

// ===== 4. Factor de desplazamiento vertical (DM) =====

export function calcularDM(D: number): number {
  if (D <= 25) return 1;
  return Math.min(1, 0.82 + 4.5 / D);
}

// ===== 5. Factor de asimetría (AM) =====

export function calcularAM(A: number): number {
  if (A > 135) return 0;
  return clamp(1 - 0.0032 * A, 0, 1);
}

// ===== 6. Factor de frecuencia (FM) — Tabla 1 =====

export type DuracionTarea = 'corta' | 'moderada' | 'larga';

interface FilaFrecuenciaFM {
  frecuenciaMax: number; // frecuencia máxima (elev/min) a la que aplica esta fila
  corta: [number, number];    // [V<75, V>=75]
  moderada: [number, number]; // [V<75, V>=75]
  larga: [number, number];    // [V<75, V>=75]
}

// Tabla 1 NIOSH: Factor de Frecuencia, por frecuencia (elev/min),
// duración de la tarea y distancia vertical V (<75cm o >=75cm).
// Extraída de NIOSH [1994], DHHS Publication No. 94-110, y
// confirmada celda por celda contra la fuente por el usuario (2026) —
// no modificar sin repetir esa verificación manual.
const TABLA_1_FRECUENCIA: FilaFrecuenciaFM[] = [
  { frecuenciaMax: 0.2, corta: [1.00, 1.00], moderada: [0.95, 0.95], larga: [0.85, 0.85] },
  { frecuenciaMax: 0.5, corta: [0.97, 0.97], moderada: [0.92, 0.92], larga: [0.81, 0.81] },
  { frecuenciaMax: 1,   corta: [0.94, 0.94], moderada: [0.88, 0.88], larga: [0.75, 0.75] },
  { frecuenciaMax: 2,   corta: [0.91, 0.91], moderada: [0.84, 0.84], larga: [0.65, 0.65] },
  { frecuenciaMax: 3,   corta: [0.88, 0.88], moderada: [0.79, 0.79], larga: [0.55, 0.55] },
  { frecuenciaMax: 4,   corta: [0.84, 0.84], moderada: [0.72, 0.72], larga: [0.45, 0.45] },
  { frecuenciaMax: 5,   corta: [0.80, 0.80], moderada: [0.60, 0.60], larga: [0.35, 0.35] },
  { frecuenciaMax: 6,   corta: [0.75, 0.75], moderada: [0.50, 0.50], larga: [0.27, 0.27] },
  { frecuenciaMax: 7,   corta: [0.70, 0.70], moderada: [0.42, 0.42], larga: [0.22, 0.22] },
  { frecuenciaMax: 8,   corta: [0.60, 0.60], moderada: [0.35, 0.35], larga: [0.18, 0.18] },
  { frecuenciaMax: 9,   corta: [0.52, 0.52], moderada: [0.30, 0.30], larga: [0.00, 0.15] },
  { frecuenciaMax: 10,  corta: [0.45, 0.45], moderada: [0.26, 0.26], larga: [0.00, 0.13] },
  { frecuenciaMax: 11,  corta: [0.41, 0.41], moderada: [0.00, 0.23], larga: [0.00, 0.00] },
  { frecuenciaMax: 12,  corta: [0.37, 0.37], moderada: [0.00, 0.21], larga: [0.00, 0.00] },
  { frecuenciaMax: 13,  corta: [0.00, 0.34], moderada: [0.00, 0.00], larga: [0.00, 0.00] },
  { frecuenciaMax: 14,  corta: [0.00, 0.31], moderada: [0.00, 0.00], larga: [0.00, 0.00] },
  { frecuenciaMax: 15,  corta: [0.00, 0.28], moderada: [0.00, 0.00], larga: [0.00, 0.00] },
];

// Frecuencia > 15 elev/min → FM = 0 en todos los casos (no incluida en
// la tabla, se maneja como caso especial).
export function calcularFM(frecuencia: number, duracion: DuracionTarea, V: number): number {
  if (frecuencia > 15) return 0;
  const fila = TABLA_1_FRECUENCIA.find(f => frecuencia <= f.frecuenciaMax);
  if (!fila) return 0;
  const columna = V < 75 ? 0 : 1;
  return fila[duracion][columna];
}

// ===== 7. Factor de agarre (CM) — Tabla 3 =====

export type TipoAgarreNiosh = 'bueno' | 'regular' | 'malo';

// Tabla 3 NIOSH: Factor de Agarre, por calidad del agarre y distancia
// vertical V (<75cm o >=75cm). Extraída de NIOSH [1994], DHHS
// Publication No. 94-110, y confirmada celda por celda contra la
// fuente por el usuario (2026) — no modificar sin repetir esa
// verificación manual.
const TABLA_3_AGARRE: Record<TipoAgarreNiosh, [number, number]> = {
  bueno: [1.00, 1.00],
  regular: [0.95, 1.00],
  malo: [0.90, 0.90],
};

export function calcularCM(agarre: TipoAgarreNiosh, V: number): number {
  return TABLA_3_AGARRE[agarre][V < 75 ? 0 : 1];
}

// ===== 8. Ecuación RWL (Límite de Peso Recomendado) =====

export interface DatosPuntoNiosh {
  H: number; // distancia horizontal, cm
  V: number; // distancia vertical, cm
  A: number; // ángulo de asimetría, grados
  agarre: TipoAgarreNiosh;
}

export function calcularRWL(
  datos: DatosPuntoNiosh,
  D: number,
  frecuencia: number,
  duracion: DuracionTarea,
  LC: number = LC_DEFAULT
): number {
  return (
    LC *
    calcularHM(datos.H) *
    calcularVM(datos.V) *
    calcularDM(D) *
    calcularAM(datos.A) *
    calcularFM(frecuencia, duracion, datos.V) *
    calcularCM(datos.agarre, datos.V)
  );
}

// ===== 9. Evaluación de una tarea de levantamiento =====

export interface EvaluacionTareaNioshInput {
  origen: DatosPuntoNiosh;
  destino?: DatosPuntoNiosh; // presente solo si hay control significativo en destino
  vOrigenParaD: number;  // V del origen, usado para calcular D
  vDestinoParaD: number; // V del destino, usado para calcular D — SIEMPRE
                          // requerido: la fuente indica que V se mide en
                          // origen y destino independientemente de que
                          // exista o no control significativo.
  frecuencia: number;
  duracion: DuracionTarea;
  peso: number; // kg, peso real de la carga
  unaMano: boolean;
  multiplesTrabajadores: boolean;
  LC?: number; // default LC_DEFAULT
}

export interface ResultadoTareaNiosh {
  D: number;
  rwlOrigen: number;
  rwlDestino: number | null; // null si no hay control significativo en destino
  rwlFinal: number; // rwlOrigen, o min(rwlOrigen, rwlDestino) si hay destino;
                     // luego con las correcciones de unaMano/multiplesTrabajadores aplicadas
  li: number; // peso / rwlFinal
  nivelRiesgo: 'aceptable' | 'moderado' | 'alto';
}

export function calcularEvaluacionTareaNiosh(input: EvaluacionTareaNioshInput): ResultadoTareaNiosh {
  const LC = input.LC ?? LC_DEFAULT;
  const D = calcularD(input.vOrigenParaD, input.vDestinoParaD);

  const rwlOrigen = calcularRWL(input.origen, D, input.frecuencia, input.duracion, LC);
  const rwlDestino = input.destino
    ? calcularRWL(input.destino, D, input.frecuencia, input.duracion, LC)
    : null;

  let rwlFinal = rwlDestino !== null ? Math.min(rwlOrigen, rwlDestino) : rwlOrigen;
  if (input.unaMano) rwlFinal *= 0.6;
  if (input.multiplesTrabajadores) rwlFinal *= 0.85;

  const li = rwlFinal > 0 ? input.peso / rwlFinal : Infinity;
  const nivelRiesgo: 'aceptable' | 'moderado' | 'alto' =
    li <= 1 ? 'aceptable' : li < 3 ? 'moderado' : 'alto';

  return { D, rwlOrigen, rwlDestino, rwlFinal, li, nivelRiesgo };
}

// ===== 10. Índice de Levantamiento Compuesto (multitarea) =====

// Recalcula el RWL final de una tarea (con destino, y las correcciones
// de una mano/múltiples trabajadores ya aplicadas) sustituyendo la
// frecuencia usada SOLO para el factor FM — el resto de factores (HM,
// VM, DM, AM, CM) no dependen de la frecuencia y se calculan siempre
// con los datos propios de la tarea. Función interna, usada
// exclusivamente por calcularIndiceLevantamientoCompuesto() para
// recalcular el LI de una tarea a una frecuencia acumulada distinta
// de la suya propia.
function calcularRWLTareaAFrecuencia(input: EvaluacionTareaNioshInput, frecuenciaParaFM: number): number {
  const LC = input.LC ?? LC_DEFAULT;
  const D = calcularD(input.vOrigenParaD, input.vDestinoParaD);

  const rwlOrigen = calcularRWL(input.origen, D, frecuenciaParaFM, input.duracion, LC);
  const rwlDestino = input.destino
    ? calcularRWL(input.destino, D, frecuenciaParaFM, input.duracion, LC)
    : null;

  let rwlFinal = rwlDestino !== null ? Math.min(rwlOrigen, rwlDestino) : rwlOrigen;
  if (input.unaMano) rwlFinal *= 0.6;
  if (input.multiplesTrabajadores) rwlFinal *= 0.85;

  return rwlFinal;
}

export interface TareaNioshMultitarea {
  id: string; // identificador legible de la tarea (ej. descripción corta)
  input: EvaluacionTareaNioshInput;
}

export interface DesgloseTareaCompuesto {
  id: string;
  liPropio: number; // LI de la tarea a su propia frecuencia (orden de clasificación)
  frecuenciaAcumuladaHastaAqui: number; // suma de frecuencias de esta tarea y todas las de mayor LI
  liAFrecuenciaAcumulada: number; // LI recalculado a esa frecuencia acumulada
  incremento: number; // 0 para la primera tarea (es la base, ILT1); para las demás, ΔILTi
}

export interface ResultadoMultitareaNiosh {
  ordenPorLI: DesgloseTareaCompuesto[]; // en el orden de mayor a menor LI propio
  ilc: number; // Índice de Levantamiento Compuesto (método recomendado, riesgo acumulado)
  nivelRiesgo: 'aceptable' | 'moderado' | 'alto'; // mismos umbrales que LI simple (<=1, <3, >=3)
  // Métodos alternativos mencionados por la fuente (no recomendados,
  // mostrar solo como referencia):
  sumaDeRiesgos: number; // suma simple de los LI propios de todas las tareas
  riesgoPromedio: number; // promedio de los LI propios
  mayorRiesgo: number; // el mayor LI propio (= ILT1)
}

// Índice de Levantamiento Compuesto (ILC) para análisis multitarea.
// Fórmula (NIOSH [1994], DHHS Publication No. 94-110, sección de
// cálculo del Índice de Levantamiento multitarea):
//
//   ILC = ILT1 + Σ ΔILTi
//   ΔILTi = ILTi(F1+F2+...+Fi) - ILTi(F1+F2+...+F(i-1))
//
// Procedimiento:
//   1. Se calcula el LI de cada tarea a su propia frecuencia.
//   2. Se ordenan las tareas de mayor a menor LI propio — la de mayor
//      LI pasa a ser la tarea 1 (ILT1).
//   3. Para cada tarea siguiente (en ese orden), se recalcula su LI
//      dos veces usando SOLO el factor FM a la frecuencia acumulada
//      (la de esta tarea y todas las anteriores en el orden) y a la
//      frecuencia acumulada sin incluir esta tarea. La diferencia es
//      su incremento de riesgo ΔILTi.
//   4. ILC = LI propio de la tarea 1 + suma de todos los incrementos.
//
// Si tareas.length === 1, el resultado es el caso trivial: ilc = LI
// de esa única tarea, sin incrementos, y las 3 alternativas
// (sumaDeRiesgos, riesgoPromedio, mayorRiesgo) iguales a ese mismo
// valor — no es un error, es la definición degenerada de un análisis
// multitarea con una sola tarea.
export function calcularIndiceLevantamientoCompuesto(tareas: TareaNioshMultitarea[]): ResultadoMultitareaNiosh {
  const evaluadas = tareas.map(t => ({
    id: t.id,
    input: t.input,
    li: calcularEvaluacionTareaNiosh(t.input).li,
    frecuencia: t.input.frecuencia,
  }));

  const ordenadas = [...evaluadas].sort((a, b) => b.li - a.li);

  let frecuenciaAcumulada = 0;
  const ordenPorLI: DesgloseTareaCompuesto[] = ordenadas.map((tarea, i) => {
    frecuenciaAcumulada += tarea.frecuencia;

    if (i === 0) {
      return {
        id: tarea.id,
        liPropio: tarea.li,
        frecuenciaAcumuladaHastaAqui: frecuenciaAcumulada,
        liAFrecuenciaAcumulada: tarea.li,
        incremento: 0,
      };
    }

    const frecuenciaSinEsta = frecuenciaAcumulada - tarea.frecuencia;
    const rwlConEsta = calcularRWLTareaAFrecuencia(tarea.input, frecuenciaAcumulada);
    const rwlSinEsta = calcularRWLTareaAFrecuencia(tarea.input, frecuenciaSinEsta);
    const liConEsta = rwlConEsta > 0 ? tarea.input.peso / rwlConEsta : Infinity;
    const liSinEsta = rwlSinEsta > 0 ? tarea.input.peso / rwlSinEsta : Infinity;

    return {
      id: tarea.id,
      liPropio: tarea.li,
      frecuenciaAcumuladaHastaAqui: frecuenciaAcumulada,
      liAFrecuenciaAcumulada: liConEsta,
      incremento: liConEsta - liSinEsta,
    };
  });

  const ilc = ordenPorLI[0].liPropio + ordenPorLI.slice(1).reduce((suma, t) => suma + t.incremento, 0);
  const nivelRiesgo: 'aceptable' | 'moderado' | 'alto' =
    ilc <= 1 ? 'aceptable' : ilc < 3 ? 'moderado' : 'alto';

  const sumaDeRiesgos = evaluadas.reduce((suma, t) => suma + t.li, 0);
  const riesgoPromedio = sumaDeRiesgos / evaluadas.length;
  const mayorRiesgo = ordenPorLI[0].liPropio;

  return { ordenPorLI, ilc, nivelRiesgo, sumaDeRiesgos, riesgoPromedio, mayorRiesgo };
}

// ============================================================
// Verificación manual — levantamiento ideal (caso de referencia)
// ============================================================
// Datos: H=25, V=75 (origen y destino, sin control significativo real
// en destino), A=0, frecuencia<0.2, duración corta, agarre bueno, LC=23.
//
// const hm = calcularHM(25); // H<25 es falso (25 no es <25) → 25/25 = 1 → HM = 1
// const vm = calcularVM(75); // 1 - 0.003*|75-75| = 1 - 0 = 1 → VM = 1
//
// const D = calcularD(75, 75); // |75-75| = 0 → D = 0
// const dm = calcularDM(0); // D<=25 → DM = 1
//
// const am = calcularAM(0); // 1 - 0.0032*0 = 1 → AM = 1
//
// const fm = calcularFM(0.1, 'corta', 75); // frecuencia 0.1 <= 0.2 →
//   fila frecuenciaMax=0.2, V=75 no es <75 → columna 1 → corta[1] = 1.00 → FM = 1
//
// const cm = calcularCM('bueno', 75); // TABLA_3_AGARRE.bueno[1] = 1.00 → CM = 1
//
// const rwl = calcularRWL({ H: 25, V: 75, A: 0, agarre: 'bueno' }, D, 0.1, 'corta', 23);
// // RWL = 23 * 1 * 1 * 1 * 1 * 1 * 1 = 23
//
// Resultado esperado (confirmado a mano antes de guardar este archivo):
//   HM=1, VM=1, D=0, DM=1, AM=1, FM=1, CM=1 → RWL = LC = 23 —
//   levantamiento ideal, sin penalización por ningún factor.
// ============================================================

// ============================================================
// Verificación manual — Índice de Levantamiento Compuesto (2 tareas)
// ============================================================
// Tarea A: H=30, V=100, A=0, agarre bueno, sin destino (vOrigenParaD=
//   vDestinoParaD=100 → D=0), frecuencia=2/min, duración corta,
//   peso=15kg, sin correcciones de mano/trabajadores.
// Tarea B: H=50, V=175, A=30, agarre malo, sin destino (vOrigenParaD=
//   vDestinoParaD=175 → D=0), frecuencia=1/min, duración corta,
//   peso=20kg, sin correcciones de mano/trabajadores.
//
// --- Paso 1: LI propio de cada tarea (a su propia frecuencia) ---
//
// Tarea A:
//   HM = 25/30 = 0.833333
//   VM = 1 - 0.003*|100-75| = 1 - 0.075 = 0.925
//   D = |100-100| = 0 → DM = 1
//   AM = 1 - 0.0032*0 = 1
//   FM(frecuencia=2, corta, V=100>=75) → fila frecuenciaMax=2,
//     columna V>=75 → corta[1] = 0.91
//   CM(bueno, V=100>=75) = 1.00
//   RWL_A = 23 * 0.833333 * 0.925 * 1 * 1 * 0.91 * 1 = 16.133542
//   LI_A(propio) = 15 / 16.133542 ≈ 0.9297
//
// Tarea B:
//   HM = 25/50 = 0.5
//   VM = 1 - 0.003*|175-75| = 1 - 0.3 = 0.7
//   D = |175-175| = 0 → DM = 1
//   AM = 1 - 0.0032*30 = 1 - 0.096 = 0.904
//   FM(frecuencia=1, corta, V=175>=75) → fila frecuenciaMax=1,
//     columna V>=75 → corta[1] = 0.94
//   CM(malo, V=175>=75) = 0.90
//   RWL_B = 23 * 0.5 * 0.7 * 1 * 0.904 * 0.94 * 0.90 = 6.156511
//   LI_B(propio) = 20 / 6.156511 ≈ 3.2486
//
// --- Paso 2: orden por LI descendente ---
//   LI_B (3.2486) > LI_A (0.9297) → orden = [B, A]. B es ILT1.
//
// --- Paso 3: incrementos ---
//   Tarea B (posición 0, la de mayor LI): incremento = 0,
//     frecuenciaAcumuladaHastaAqui = 1 (solo su propia frecuencia).
//
//   Tarea A (posición 1):
//     frecuenciaAcumuladaHastaAqui = freq_B + freq_A = 1 + 2 = 3
//     frecuenciaAcumuladaSinEsta = freq_B = 1
//
//     LI_A a frecuencia acumulada=3: mismo HM/VM/DM/AM/CM de A, pero
//       FM(frecuencia=3, corta, V=100>=75) → fila frecuenciaMax=3,
//       columna V>=75 → corta[1] = 0.88
//       RWL = 23*0.833333*0.925*1*1*0.88*1 = 15.601667
//       LI_A(freq=3) = 15 / 15.601667 ≈ 0.9614
//
//     LI_A a frecuencia acumulada=1 (sin A): FM(frecuencia=1, corta,
//       V=100>=75) → fila frecuenciaMax=1, columna V>=75 → corta[1] = 0.94
//       RWL = 23*0.833333*0.925*1*1*0.94*1 = 16.665417
//       LI_A(freq=1) = 15 / 16.665417 ≈ 0.9001
//
//     ΔILT_A = 0.9614 - 0.9001 = 0.0614 (incremento de A)
//
// --- Paso 4: ILC ---
//   ILC = LI_B(propio) + ΔILT_A = 3.2486 + 0.0614 ≈ 3.3100
//   nivelRiesgo: ILC >= 3 → 'alto'
//
// --- Métodos alternativos (referencia, no recomendados) ---
//   sumaDeRiesgos = LI_B + LI_A = 3.2486 + 0.9297 = 4.1783
//   riesgoPromedio = 4.1783 / 2 ≈ 2.0892
//   mayorRiesgo = LI_B(propio) ≈ 3.2486
//
// Resultado esperado (confirmado a mano antes de guardar este
// archivo): ordenPorLI = [B (liPropio≈3.2486, incremento=0),
// A (liPropio≈0.9297, incremento≈0.0614)]; ilc≈3.3100, nivel 'alto';
// sumaDeRiesgos≈4.1783, riesgoPromedio≈2.0892, mayorRiesgo≈3.2486.
// ============================================================
